import { create } from "zustand";
import {
  getExtensionSettings,
  updateExtensionSettings,
  type UserPreferences,
  DEFAULT_SETTINGS,
} from "../api/userPreferencesApi";

export type { UserPreferences };
export { DEFAULT_SETTINGS };

interface UserPreferencesState {
  settings: UserPreferences;
  loading: boolean;
  error: string | null;
  initialized: boolean;

  // Actions
  loadSettings: () => Promise<void>;
  updateSetting: (
    key: keyof UserPreferences,
    value: string | boolean | null
  ) => Promise<void>;
  reset: () => void;
}

export const useUserPreferencesStore = create<UserPreferencesState>(
  (set, get) => ({
    settings: DEFAULT_SETTINGS,
    loading: false,
    error: null,
    initialized: false,

    loadSettings: async () => {
      if (get().initialized || get().loading) {
        return;
      }

      set({ loading: true, error: null });

      try {
        const response = await getExtensionSettings();
        set({
          settings: response.settings,
          loading: false,
          initialized: true,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load settings";
        set({ error: errorMessage, loading: false, initialized: true });
        console.error("[UserPreferencesStore] Failed to load settings:", error);
        throw error;
      }
    },

    updateSetting: async (
      key: keyof UserPreferences,
      value: string | boolean | null
    ) => {
      try {
        set({ error: null });

        const response = await updateExtensionSettings({ [key]: value });
        set({ settings: response.settings });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update setting";
        set({ error: errorMessage });

        console.error(`[UserPreferencesStore] Failed to update ${key}:`, error);
        throw error;
      }
    },

    reset: () => {
      set({
        settings: DEFAULT_SETTINGS,
        loading: false,
        error: null,
        initialized: false,
      });
    },
  })
);
